import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import ContextualAIChat from "@/components/ContextualAIChat";
import SpendingInsights from "@/components/SpendingInsights";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  DollarSign,
  ShoppingBag,
  Car,
  Utensils,
  Zap,
  Heart,
  Film,
  MoreHorizontal,
  Loader2,
  ChevronDown,
  ChevronUp,
  BarChart3
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  "Food": Utensils,
  "Transport": Car,
  "Shopping": ShoppingBag,
  "Bills": Zap,
  "Entertainment": Film,
  "Health": Heart,
  "Other": MoreHorizontal
};

const categories = ["Food", "Transport", "Shopping", "Bills", "Entertainment", "Health", "Other"];

const BudgetPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newExpense, setNewExpense] = useState({
    amount: "",
    category: "Food",
    description: ""
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchData(userData.id);
  }, [navigate]);

  const fetchData = async (userId) => {
    try {
      const [expensesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/expenses/${userId}`),
        axios.get(`${API}/expenses/${userId}/summary`)
      ]);
      setExpenses(expensesRes.data?.expenses || []);
      setSummary(summaryRes.data || { total: 0, by_category: {} });
    } catch (error) {
      console.error("Error fetching expenses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async () => {
    if (!newExpense.amount || !newExpense.description) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(`${API}/expenses/${user.id}`, {
        amount: parseFloat(newExpense.amount),
        category: newExpense.category,
        description: newExpense.description
      });
      
      toast.success("Expense added successfully");
      setDialogOpen(false);
      setNewExpense({ amount: "", category: "Food", description: "" });
      fetchData(user.id);
    } catch (error) {
      toast.error("Failed to add expense");
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success("Expense deleted");
      fetchData(user.id);
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tamba-coral" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-heading text-xl font-semibold">Budget Tracker</h1>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-tamba-coral hover:bg-tamba-coral/90" data-testid="add-expense-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Add New Expense</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    className="mt-2"
                    data-testid="expense-amount-input"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select
                    value={newExpense.category}
                    onValueChange={(value) => setNewExpense({ ...newExpense, category: value })}
                  >
                    <SelectTrigger className="mt-2" data-testid="expense-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    placeholder="What was this expense for?"
                    value={newExpense.description}
                    onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                    className="mt-2"
                    data-testid="expense-description-input"
                  />
                </div>
                <Button 
                  onClick={handleAddExpense} 
                  className="w-full rounded-full bg-tamba-coral hover:bg-tamba-coral/90"
                  data-testid="save-expense-btn"
                >
                  Save Expense
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Spending Insights */}
        <SpendingInsights 
          expenses={expenses} 
          income_range={user?.income_range}
        />

        {/* Total Summary */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-tamba-coral/30 bg-gradient-to-br from-tamba-coral/5 to-transparent">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-tamba-coral/10 flex items-center justify-center">
                  <DollarSign className="h-7 w-7 text-tamba-coral" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Spent</p>
                  <p className="font-heading text-3xl font-bold">${summary.total.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Category Breakdown */}
        {Object.keys(summary.by_category).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6"
          >
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">Spending by Category</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-4">
                  {Object.entries(summary.by_category).map(([category, amount], idx) => {
                    const Icon = categoryIcons[category] || MoreHorizontal;
                    const percentage = summary.total > 0 ? (amount / summary.total) * 100 : 0;
                    const colors = ["bg-tamba-blue", "bg-tamba-yellow", "bg-tamba-coral", "bg-tamba-teal"];
                    const iconColors = ["text-tamba-blue bg-tamba-blue/10", "text-tamba-yellow bg-tamba-yellow/10", "text-tamba-coral bg-tamba-coral/10", "text-tamba-teal bg-tamba-teal/10"];
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColors[idx % iconColors.length]}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <span className="text-sm font-medium">{category}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-semibold">${amount.toFixed(2)}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ({percentage.toFixed(0)}%)
                            </span>
                          </div>
                        </div>
                        <div className="h-2 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${colors[idx % colors.length]} rounded-full transition-all`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Recent Expenses */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-heading text-lg font-semibold mb-4">Recent Expenses</h2>
          
          {expenses.length === 0 ? (
            <Card className="border-border/50 border-dashed">
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-4 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground mb-4">No expenses tracked yet</p>
                <Button 
                  onClick={() => setDialogOpen(true)}
                  className="rounded-full"
                  data-testid="empty-add-expense-btn"
                >
                  Add Your First Expense
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {expenses.map((expense, index) => {
                const Icon = categoryIcons[expense.category] || MoreHorizontal;
                return (
                  <motion.div
                    key={expense.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-border/50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                              <Icon className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{expense.description}</p>
                              <p className="text-xs text-muted-foreground">{expense.category}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-heading font-semibold">${expense.amount.toFixed(2)}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteExpense(expense.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-expense-${expense.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </main>

      {/* Contextual AI Chat */}
      {user && (
        <ContextualAIChat
          context="budget"
          userId={user.id}
          pageData={{ 
            total_spent: summary.total,
            by_category: summary.by_category,
            expense_count: expenses.length
          }}
          suggestedPrompts={[
            "Where am I spending the most?",
            "How can I reduce my expenses?",
            "What's a good budget breakdown?",
            "Am I overspending in any category?"
          ]}
          accentColor="tamba-coral"
        />
      )}
    </div>
  );
};

export default BudgetPage;
